import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, type Control } from "react-hook-form";
import { View, Text, ScrollView } from "react-native";
import { Select } from "@/elements/components/ui/select";
import { Textarea } from "@/elements/components/ui/textarea";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import type {
  SingleSelectSheetPayload,
  SingleSelectSheetResult,
} from "@/elements/components/sheets/single-select-sheet";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@/elements/components/utils/convex-api";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { Tab } from "../ui/tab";
import type { ChildFormInput } from "@/elements/hooks/use-child-form";

type Subject = {
  subjectId: string;
  qualificationId?: string;
  examDetails?: string;
};

interface SubjectQualificationsFormProps {
  control: Control<ChildFormInput>;
  subjects: Subject[];
  yearGroupId: string;
}

function SubjectQualificationsForm({
  control,
  subjects,
  yearGroupId,
}: SubjectQualificationsFormProps) {
  const { open: openSingleSelectSheet } = useSheet<
    SingleSelectSheetPayload,
    SingleSelectSheetResult
  >(SheetIds.SINGLE_SELECT);

  const { data: subjectsForYearGroup = [] } = useQuery({
    ...convexQuery(api.parent.onboarding.queries.getSubjectsForYearGroup, {
      yearGroupId: yearGroupId as Id<"yearGroups">,
    }),
    enabled: !!yearGroupId,
  });

  const { data: qualificationTypes = [], isLoading } = useQuery({
    ...convexQuery(
      api.parent.onboarding.queries.getAvailableQualificationsForYearGroup,
      {
        yearGroupId: yearGroupId as Id<"yearGroups">,
      },
    ),
    enabled: !!yearGroupId,
  });

  const subjectsNeedingQualification = useMemo(
    () =>
      subjects
        .map((subject, index) => ({ subject, index }))
        .filter(
          ({ subject }) =>
            subjectsForYearGroup.find((s) => s.value === subject.subjectId)
              ?.requiresQualification ?? false,
        ),
    [subjects, subjectsForYearGroup],
  );

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    if (
      subjectsNeedingQualification.length > 0 &&
      activeTabIndex >= subjectsNeedingQualification.length
    ) {
      setActiveTabIndex(0);
    }
  }, [subjectsNeedingQualification, activeTabIndex]);

  const activeEntry = subjectsNeedingQualification[activeTabIndex];
  const activeFormIndex = activeEntry?.index;
  const activeSubjectId = activeEntry?.subject.subjectId;

  const handleQualificationOptionsOpen = useCallback(
    async (field: {
      value: string | undefined;
      onChange: (val: string) => void;
    }) => {
      if (!qualificationTypes) return;

      const result = await openSingleSelectSheet({
        options: qualificationTypes,
        preSelectedOption: field.value || "",
      });

      if (result?.selectedOption) {
        field.onChange(result.selectedOption as string);
      }
    },
    [openSingleSelectSheet, qualificationTypes],
  );

  if (!activeEntry || activeFormIndex === undefined || !activeSubjectId) {
    return null;
  }

  return (
    <View className="gap-6 my-4">
      <View className="gap-4">
        <Text className="font-geologica-medium leading-[1.5] font-medium text-base text-black">
          Subject qualifications
        </Text>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerClassName="flex-row gap-2"
        >
          {subjectsNeedingQualification.map(({ subject }, tabIndex) => (
            <Tab
              size="md"
              key={subject.subjectId}
              intent={tabIndex === activeTabIndex ? "active" : "inActive"}
              onPress={() => setActiveTabIndex(tabIndex)}
            >
              <Tab.Text size="md">
                {
                  subjectsForYearGroup.find((s) => s.value === subject.subjectId)
                    ?.label
                }
              </Tab.Text>
            </Tab>
          ))}
        </ScrollView>

        <View className="gap-4" key={activeSubjectId}>
          <Controller
            control={control}
            name={`subjects.${activeFormIndex}.qualificationId`}
            render={({ field }) => (
              <Select
                onPress={() => handleQualificationOptionsOpen(field)}
                disabled={isLoading || !qualificationTypes}
                size="md"
              >
                <Select.Label size="md">
                  {field.value
                    ? qualificationTypes?.find((q) => q.value === field.value)
                        ?.label
                    : "Qualification"}
                </Select.Label>
                <Select.DownArrow />
              </Select>
            )}
          />

          <View className="gap-4">
            <Text className="font-geologica-medium leading-[1.5] font-medium text-base text-black">
              Exam board details
            </Text>
            <Controller
              control={control}
              name={`subjects.${activeFormIndex}.examDetails`}
              render={({ field }) => (
                <Textarea
                  multiline
                  placeholder="e.g- AQA, Edexcel, CIE etc. If unsure, your child may know or you can usually fine this on the school website or subject handbook. If you cannot find this, please do not worry as this can be filled in later."
                  size="md"
                  value={field.value ?? ""}
                  onChangeText={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

export default SubjectQualificationsForm;
